import { useCallback, useEffect, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import {
    UrlSlug,
    urlSlugToDataProductKey
} from "@/analytics/mappings/dataProductKey.ts";
import { wacToken } from "@/libraries/design/foundations/index.ts";
import { PricingCards } from "@/libraries/design/patterns/pricing-cards/src/pricing-cards.tsx";

import { useCalculationState } from "./calculation.ts";
import { getFeaturesList, LoadingSpinner } from "./components";
import { PricingCalculatorInputs } from "./components/index.ts";
import { serviceCollectionOfferingSkus } from "./configs/index.ts";
import {
    GetDiscountedPriceArgs,
    PricingCalculatorContextProvider
} from "./context.tsx";
import { useProviderProps } from "./mappings.ts";
import * as messages from "./messages.ts";
import { PricingCalculatorCard } from "./pricing-calculator-card.tsx";
import { InputValues, Plan } from "./types.ts";

interface RecommendationTrackerProps {
    plan: Plan;
    index: number;
    onRecommendedChange: (index: number, isRecommended: boolean) => void;
}

const RecommendationTracker = ({
    plan,
    index,
    onRecommendedChange
}: RecommendationTrackerProps) => {
    const state = useCalculationState({ offeringSku: plan.fields.sku });

    useEffect(() => {
        onRecommendedChange(index, state.recommended);
    }, [index, state.recommended, onRecommendedChange]);

    return null;
};

export interface UniformPricingCalculatorProps {
    urlSlug: string;
    comparePlansData: Plan[];
    disclaimer?: string;
    inputs?: InputValues;
    onChangeInputs?: (value: InputValues) => void;
    getDiscountedPrice: (args: GetDiscountedPriceArgs) => number;
}

interface PricingCalculatorContentProps {
    comparePlansData: Plan[];
    urlSlug: UrlSlug;
    selectedCardIndex: number | undefined;
    setSelectedCardIndex: (index: number | undefined) => void;
    disclaimer?: string;
    omitInputs?: boolean;
    numSections?: number;
}

const PricingCalculatorContent = ({
    comparePlansData,
    urlSlug,
    selectedCardIndex,
    setSelectedCardIndex,
    disclaimer,
    omitInputs,
    numSections
}: PricingCalculatorContentProps) => {
    const intl = useIntl();

    const handleRecommendedChange = useCallback(
        (index: number, isRecommended: boolean) => {
            if (isRecommended) {
                setSelectedCardIndex(index);
            }
        },
        [setSelectedCardIndex]
    );

    const unitsLabel = useUnitsLabel(comparePlansData);

    return (
        <div
            css={css({
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center"
            })}
        >
            {/* this doesn't render anything and just tracks the recommended card only if there are multiple plans */}
            {comparePlansData.length > 1 &&
                comparePlansData.map((planData, index) => (
                    <RecommendationTracker
                        key={`tracker-${planData.fields.sku}`}
                        plan={planData}
                        index={index}
                        onRecommendedChange={handleRecommendedChange}
                    />
                ))}
            {!omitInputs && (
                <PricingCalculatorInputs
                    unitCountLabel={intl.formatMessage(
                        messages.units.teamSizePrompt
                    )}
                    unitsLabel={unitsLabel}
                    dataProductKey={
                        urlSlugToDataProductKey[
                            urlSlug as keyof typeof urlSlugToDataProductKey
                        ]
                    }
                />
            )}
            <PricingCards
                // must set the number of sections here if removing the bottom section
                // could also infer this from planData's getFeaturesList(fields.highlightedFeaturesContainer)
                numSections={numSections}
                selectedCardIndex={
                    selectedCardIndex ? selectedCardIndex + 1 : 1
                }
            >
                {comparePlansData.map((planData, index) => (
                    <PricingCalculatorCard
                        showBannerText={comparePlansData.length > 1}
                        key={planData.fields.sku}
                        planData={planData}
                        urlSlug={urlSlug}
                        index={index}
                        isSingleCard={comparePlansData.length === 1}
                    />
                ))}
            </PricingCards>

            {disclaimer && (
                <div css={disclaimerContainerCss}>
                    <p css={disclaimerCss}>{disclaimer}</p>
                </div>
            )}
        </div>
    );
};

const disclaimerContainerCss = css({
    display: "flex",
    justifyContent: "center"
});

const disclaimerCss = css({
    maxWidth: "384px",
    fontWeight: "400",
    lineHeight: "20px",
    fontSize: "14px",
    color: wacToken("color.primary.black"),
    padding: token("space.150"),
    margin: 0
});

const DEFAULT_SELECTED_CARD_INDEX = 2;

const UniformPricingCalculator = (props: UniformPricingCalculatorProps) => {
    const {
        comparePlansData,
        urlSlug,
        disclaimer,
        inputs,
        onChangeInputs,
        getDiscountedPrice
    } = props;
    const [selectedCardIndex, setSelectedCardIndex] = useState<
        number | undefined
    >(DEFAULT_SELECTED_CARD_INDEX);
    const providerResult = useProviderProps({
        plans: comparePlansData,
        product: urlSlug,
        inputValues: inputs
    });

    if (providerResult.status === "pending") {
        return <LoadingSpinner />;
    }

    if (providerResult.status === "error") {
        return null; // todo: fire an event @bsparks GWP-6943
    }

    if (providerResult.status !== "ready") {
        return null; // this should never happen due to checks above, but fixes type error
    }

    const { config, inputValues } = providerResult.props;

    return (
        <PricingCalculatorContextProvider
            config={config}
            inputValues={inputValues}
            onChangeInputs={onChangeInputs}
            getDiscountedPrice={getDiscountedPrice}
        >
            <PricingCalculatorContent
                comparePlansData={comparePlansData}
                urlSlug={urlSlug as UrlSlug}
                selectedCardIndex={selectedCardIndex}
                setSelectedCardIndex={setSelectedCardIndex}
                disclaimer={disclaimer}
                numSections={
                    getFeaturesList(
                        comparePlansData?.[0]?.fields
                            ?.highlightedFeaturesContainer
                    ) === undefined
                        ? 1
                        : 2
                }
                omitInputs={
                    !config.billingCycleConfig.monthly &&
                    !config.billingCycleConfig.annual
                }
            />
        </PricingCalculatorContextProvider>
    );
};

export default UniformPricingCalculator;

function useUnitsLabel(comparePlansData: Plan[]) {
    const intl = useIntl();

    const hasAgents = comparePlansData.some((plan) => {
        return (
            [
                serviceCollectionOfferingSkus.free,
                serviceCollectionOfferingSkus.standard,
                serviceCollectionOfferingSkus.premium,
                serviceCollectionOfferingSkus.enterprise
            ] as string[]
        ).includes(plan.fields.sku);
    });
    if (hasAgents) {
        return intl.formatMessage(messages.units.agentsUnitsLabel);
    }

    return intl.formatMessage(messages.units.usersUnitsLabel);
}
